import React, { useState } from 'react';

import Image from 'next/image';
import Head from 'next/head';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  CardContent,
  Container,
  Grid,
  Link as MuiLink,
} from '@mui/material';

import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';

import ReCAPTCHA from 'react-google-recaptcha';

import BottomNav from '../components/BottomNav';
import TopNav from '../components/TopNav';
import { NextLinkComposed } from '../components/Link';

import mobileRegex from '../constants/mobileRegex';

import axiosInstance from '../lib/axiosInstance';


const ContactUsSchema = Yup.object().shape({
  first_name: Yup.string().max(100, 'Only 100 characters allowed').required('Please enter your name'),
  last_name: Yup.string().max(100, 'Only 100 characters allowed').required('Please enter your name'),
  email: Yup.string().email('Invalid email').required('Please enter your email'),
  phone: Yup.string()
    .matches(mobileRegex, 'The mobile number you provided is not valid')
    .required('Please enter your contact number'),
  message: Yup.string().required('What is it that you would like to say or ask?'),
  recaptcha: Yup.string().required(),
});

function ContactUs() {
  
  const [sent, setSent] = useState(false);

  return (
    <>
      <Head>
        <title>Contact Us - MechanicBuddy</title>
        <meta
          name="description"
          content="We connect you with local reputable mechanics and workshops. Get a quick cost estimate or request your own quotes."
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.mechanicbuddy.co.za/contact-us" />
        <meta property="og:title" content="Contact Us | MechanicBuddy" />
        <meta
          property="og:description"
          content="We connect you with local reputable mechanics and workshops. Get a quick cost estimate or request your own quotes."
        />
        <meta
          property="og:image"
          content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
        />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content="https://www.mechanicbuddy.co.za/contact-us" />
        <meta property="twitter:title" content="Contact Us | MechanicBuddy" />
        <meta
          property="twitter:description"
          content="We connect you with local reputable mechanics and workshops. Get a quick cost estimate or request your own quotes."
        />
        <meta
          property="twitter:image"
          content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
        />

        <link rel="canonical" href="https://www.mechanicbuddy.co.za/contact-us/" />
      </Head>

      <Box
        sx={{
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          minHeight: {
            md: '100vh'
          },
          backgroundImage: {
            md: 'url("/img/contact-us-bg.jpg")'
          },

          '& .MuiAppBar-colorPrimary': {
            backgroundColor: {
              md: 'transparent',
            },
          },
        }}>
          <TopNav />

          <Container 
            maxWidth="md"
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 'calc(100vh - 64px)'
            }}
          >
            <Grid container justifyContent="center" alignItems="center">
              <Grid item xs={12} md={5}>
                <Box display={{ xs: 'block', md: 'none' }} mt={4}>
                  <Box textAlign="center">
                    <Box fontSize="h3.fontSize" color="#133546">
                      Contact Us
                    </Box>
                    <Box mt={1} fontSize="h5.fontSize" color="#133546">
                      We connect you with local mechanics and workshops
                    </Box>
                  </Box>

                  <Box
                    p={2}
                    color="#133546"
                    bgcolor="white"
                    my={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Box fontSize="h5.fontSize">Cost Estimates</Box>
                      <Box my={2}>Get instant cost estimates OR request your own quotes.</Box>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        component={NextLinkComposed}
                        to={{ pathname: '/request-quotes/' }}
                      >
                        Get Quotes
                      </Button>
                    </Box>
                    <Image
                      src="/img/new-estimate-dark-mode.png"
                      alt="Cost estimate"
                      width={85}
                      height={85}
                      priority
                    />
                  </Box>
                </Box>

                <Box fontSize="h1.fontSize" color="white" display={{ xs: 'none', md: 'block' }}>
                  Contact Us
                </Box>

                <Box
                  mt={1}
                  fontSize="h4.fontSize"
                  color="white"
                  display={{ xs: 'none', md: 'block' }}
                >
                  We connect you with local mechanics and workshops
                </Box>
                <Box
                  mt={4}
                  display={{ xs: 'none', md: 'flex' }}
                  sx={{
                    display: {
                      xs: 'none',
                      md: 'block'
                    },
                    '& .MuiButton-contained': {
                      minWidth: '150px',
                    },
                  }}
                >
                  <Button
                    color="secondary"
                    variant="contained"
                    sx={{ mr: 2 }}
                    size="large"
                    component={NextLinkComposed}
                    to={{ pathname: '/request-quotes/' }}
                  >
                    Get Quotes
                  </Button>
                  <Button
                    color="primary"
                    sx={{
                      bgcolor: 'primary.light',
                      '&:hover': {
                        bgcolor: '#006978',
                      },
                    }}
                    variant="contained"
                    href="/best-mechanics"
                    size="large"
                  >
                    Find a Mechanic
                  </Button>
                </Box>
                <Box
                  mt={4}
                  mb={2}
                  fontSize="h6.fontSize"
                  color="white"
                  display={{ xs: 'none', md: 'block' }}
                >
                  Follow us on social media
                </Box>
                <Box display={{ xs: 'none', md: 'block' }}>
                  <MuiLink
                    href="//www.facebook.com/mechanicbuddyZA"
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                  >
                    <Image
                      src="/icons/facebook-circular-logo.svg"
                      width={35}
                      height={35}
                      alt="Facebook"
                    />
                  </MuiLink>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Card
                  sx={{
                    '& .MuiTextField-root': {
                      mb: 2,
                    },
                    ml: {
                      md: 6
                    },
                    mb: {
                      xs: 4,
                      sm: null
                    }
                  }}
                >
                  {!sent && (
                    <CardContent>
                      <Box display="flex" alignItems="center" pb={3}>
                        <Box display={{ xs: 'none', md: 'block' }} pr={4}>
                          <Image
                            src="/img/welcome-illustration-dark-mode.png"
                            alt="How can we help"
                            width={100}
                            height={68}
                          />
                        </Box>
                        <Box>
                          <Box fontSize="h4.fontSize" pt={1}>
                            How can we help you?
                          </Box>
                          <Box fontSize="body2.fontSize" display={{ xs: 'none', md: 'block' }}>
                            We will get back to you ASAP
                          </Box>
                        </Box>
                      </Box>
                      <Formik
                        initialValues={{
                          first_name: '',
                          last_name: '',
                          email: '',
                          phone: '',
                          message: '',
                          recaptcha: '',
                        }}
                        validationSchema={ContactUsSchema}
                        onSubmit={(values, { setSubmitting }) => {
                          axiosInstance
                            .post(`/content/contact-us/`, values)
                            .then(() => {
                              setSubmitting(false);
                              setSent(true);
                            })
                            .catch(error => {
                              console.log(error);
                              setSubmitting(false);
                            });
                        }}
                      >
                        {({ submitForm, isSubmitting, setFieldValue }) => (
                          <Form>
                            <Field
                              component={TextField}
                              label="First Name"
                              name="first_name"
                              variant="outlined"
                              fullWidth
                            />
                            <Field
                              component={TextField}
                              label="Last Name"
                              name="last_name"
                              variant="outlined"
                              fullWidth
                            />
                            <Grid container>
                              <Grid item xs={12} md={6}>
                                <Field
                                  component={TextField}
                                  name="email"
                                  type="email"
                                  label="Email Address"
                                  variant="outlined"
                                  fullWidth
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                  pl: {
                                    md: 1
                                  }
                                }}
                              >
                                <Field
                                  component={TextField}
                                  name="phone"
                                  label="Contact Number"
                                  variant="outlined"
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                            <Field
                              component={TextField}
                              name="message"
                              label="Your Message"
                              variant="outlined"
                              fullWidth
                              multiline
                              rows={4}
                            />
                            <Box
                              display={{ xs: 'block', md: 'flex' }}
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Box display="flex" justifyContent="center">
                                <ReCAPTCHA
                                  sitekey="6LePb-8UAAAAAB9ODTrFKU3x4kTHRZ-4Mr6ZipON"
                                  onChange={response => setFieldValue('recaptcha', response)}
                                />
                              </Box>

                              <Backdrop
                                open={isSubmitting}
                                sx={{
                                  zIndex: theme => theme.zIndex.drawer + 1,
                                  color: '#fff',
                                }}
                              >
                                <CircularProgress color="inherit" />
                              </Backdrop>

                              <Box display="flex" justifyContent="center" mt={2}>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  disabled={isSubmitting}
                                  onClick={submitForm}
                                >
                                  Submit
                                </Button>
                              </Box>
                            </Box>
                          </Form>
                        )}
                      </Formik>
                    </CardContent>
                  )}

                  {sent && (
                    <CardContent>
                      <Box textAlign="center">
                        <Box fontSize="h4.fontSize" textAlign="center" mb={2}>
                          Thanks for your enquiry.
                        </Box>
                        <Box fontSize="h5.fontSize" textAlign="center" mb={2}>
                          We will get back to you as soon as possible.
                        </Box>
                        <Image
                          src="/icons/choose-a-mechanic.svg"
                          width={200}
                          height={200}
                          alt="Mechanic"
                        />
                      </Box>
                    </CardContent>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Container>

      </Box>

      <BottomNav />
    </>
  );
}

export default ContactUs;
